/**
 * The invoice-status master, read once per mounted surface — the input
 * `creditNoteGateOf` in `@/lib/credit-notes` needs to decide whether a credit
 * note may be raised from an invoice.
 *
 * Moved here verbatim from `InvoicesPage` (2026-10-12) so the company page's
 * "Sales & billing history" can offer the same "+" without a second copy.
 * ⚠️ `InvoiceDetailPage` still declares its own identical copy; folding it in
 * is a separate, behaviour-free change nobody has made yet.
 *
 * ⚠️ **What stays HERE is the FETCH** — `@/lib/credit-notes` decides and never
 * fetches, which is its own standing rule.
 *
 * 🔴 **`/invoice-statuses`, NOT `/invoice-tracker/status-labels`.** *SUPERSEDED:
 * this hook read the tracker's labels.* Only the master carries
 * `isAwaitingPayment`, which is the one flag separating a Draft from an invoice
 * awaiting payment and therefore the one the gate turns on. ⛔ **Reading the
 * labels here would not fail** — the key would be `undefined` on every row and
 * the gate would silently collapse back to the superseded paid-only rule.
 *
 * ⚠️ **NO `isActive` FILTER, deliberately.** A status retired after an invoice
 * was raised still has to be classifiable, because the invoice still carries it.
 * `perPage` is the masters' ceiling of 200, so the whole list arrives in one
 * page and nothing here has to page.
 *
 * ⚠️ **`failed` is a THIRD state and is not `statuses === null`.** Loading and
 * failed both have no list, and they must not produce the same sentence: one
 * resolves on its own and the other never will.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { InvoiceStatus } from '@/lib/types';

export function useInvoiceStatusFlags() {
  const [statuses, setStatuses] = useState<readonly InvoiceStatus[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listInvoiceStatuses({ page: 1, perPage: 200 }, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setStatuses(answer.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        /*
         * The list is left `null` rather than set to `[]`. An empty list is a
         * real answer meaning "no statuses exist" and would send every gate
         * down the can't-classify branch with the wrong sentence; `failed`
         * says the read never landed.
         */
        setFailed(true);
      });
    return () => controller.abort();
  }, []);

  return { statuses, failed };
}
